import { useEffect, useMemo, useRef, useState } from 'react';
import { hexPoints, v } from '../constants';
import { dailyDateString } from '../generator';
import { fetchSolveHistory, type SolveHistoryEntry } from '../lib/solves';
import type { Difficulty } from '../types';
import {
  humanReadableBytes,
  runArchiveDownload,
  summarizePinned,
  type ArchiveDlState,
} from '../lib/archiveDownloader';
import { clearPinned } from '../lib/offlineCache';
import { getAlwaysDownloadEnabled, setAlwaysDownloadEnabled } from '../lib/alwaysDownload';

// ─────────────────────────────────────────────────────────────────────
// Archive view — past-day puzzle picker matching the iOS layout
// ─────────────────────────────────────────────────────────────────────
//
// Replaces the inline date picker in the header. Vertical list of dates
// from MIN_ARCHIVE_DATE back to today (today first). Each row shows the
// date + four mini-hex chips, one per difficulty, filled when that
// (date, difficulty) has been solved. Tapping any hex loads that
// puzzle at that difficulty and dismisses the view.
//
// Premium-gated: free users see an upgrade tease.

const MIN_ARCHIVE_DATE = '2026-01-01';

const DIFFICULTIES_IN_ORDER: Difficulty[] = ['Easy', 'Medium', 'Hard', 'Expert'];

interface ArchiveViewProps {
  isPremium: boolean;
  isSignedIn: boolean;
  currentDate: string;
  onPick: (date: string, difficulty: Difficulty) => void;
  onClose: () => void;
  onUpgrade: () => void;
}

export function ArchiveView({
  isPremium, isSignedIn, currentDate, onPick, onClose, onUpgrade,
}: ArchiveViewProps) {
  const [solvedByDate, setSolvedByDate] = useState<Record<string, Set<Difficulty>>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const history = await fetchSolveHistory();
      if (cancelled) return;
      if (!history) { setLoading(false); return; }
      const map: Record<string, Set<Difficulty>> = {};
      for (const e of history) {
        const set = map[e.puzzleDate] ?? new Set();
        set.add(e.difficulty);
        map[e.puzzleDate] = set;
      }
      setSolvedByDate(map);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Build the date list once — descends from today (ET) to MIN_ARCHIVE_DATE.
  const dates = useMemo(() => buildDateList(), []);

  // Esc to close, like the other modals.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="hm-archive-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Past puzzle archive"
      onClick={onClose}
    >
      <div className="hm-archive-card" onClick={e => e.stopPropagation()}>
        <header className="hm-archive-header">
          <h2 className="hm-archive-title">Archive</h2>
          <button
            type="button"
            className="hm-modal-close"
            onClick={onClose}
            aria-label="Close archive"
          >×</button>
        </header>

        {!isSignedIn ? (
          <Paywall message="Sign in and subscribe to play past puzzles." onUpgrade={onUpgrade} />
        ) : !isPremium ? (
          <Paywall message="The archive is a Premium feature. Upgrade to play every past hex." onUpgrade={onUpgrade} />
        ) : loading ? (
          <div className="hm-archive-loading">Loading…</div>
        ) : (
          <>
            <OfflineSection />
            <div className="hm-archive-list" role="list">
              {dates.map(date => (
                <ArchiveRow
                  key={date}
                  date={date}
                  solved={solvedByDate[date] ?? EMPTY_SET}
                  isCurrent={date === currentDate}
                  onPickDifficulty={(d) => { onPick(date, d); onClose(); }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const EMPTY_SET: Set<Difficulty> = new Set();

// ─────────────────────────────────────────────────────────────────────

interface ArchiveRowProps {
  date: string;
  solved: Set<Difficulty>;
  isCurrent: boolean;
  onPickDifficulty: (d: Difficulty) => void;
}

function ArchiveRow({ date, solved, isCurrent, onPickDifficulty }: ArchiveRowProps) {
  return (
    <div
      className={`hm-archive-row ${isCurrent ? 'is-current' : ''}`}
      role="listitem"
    >
      <div className="hm-archive-date">
        <div className="hm-archive-date-pretty">{prettyDate(date)}</div>
        <div className="hm-archive-date-year">{date.slice(0, 4)}</div>
      </div>
      <div className="hm-archive-chips">
        {DIFFICULTIES_IN_ORDER.map(d => (
          <DifficultyHexChip
            key={d}
            difficulty={d}
            completed={solved.has(d)}
            onClick={() => onPickDifficulty(d)}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────

const DIFFICULTY_INITIAL: Record<Difficulty, string> = {
  Easy: 'E', Medium: 'M', Hard: 'H', Expert: 'X',
};

function DifficultyHexChip({
  difficulty, completed, onClick,
}: {
  difficulty: Difficulty;
  completed: boolean;
  onClick: () => void;
}) {
  // Expert = gold/accent, all others = green (ok). Matches iOS palette.
  const fill = !completed
    ? 'transparent'
    : difficulty === 'Expert' ? v('accent') : v('ok');
  const stroke = completed ? fill : v('rule');
  const glyphFill = completed ? v('paper') : v('inkMute');
  const R = 16;
  return (
    <button
      type="button"
      className={`hm-archive-chip ${completed ? 'completed' : ''}`}
      onClick={onClick}
      aria-label={`${difficulty}${completed ? ' (solved)' : ''}`}
      title={difficulty}
    >
      <svg width={R * 2} height={R * Math.sqrt(3)} viewBox={`-${R} -${R * Math.sqrt(3) / 2} ${R * 2} ${R * Math.sqrt(3)}`}>
        <polygon
          points={hexPoints(0, 0, R)}
          fill={fill}
          stroke={stroke}
          strokeWidth={1.2}
          strokeLinejoin="round"
        />
        <text
          x={0} y={0}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="inherit"
          fontWeight={600}
          fontSize={R * 0.7}
          fill={glyphFill}
        >{DIFFICULTY_INITIAL[difficulty]}</text>
      </svg>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// OFFLINE: bulk-download every past puzzle into the pinned cache so the
// archive is playable on planes / subways. Mirrors the offlineSection
// in App/Hexponents/Hexponents/ArchiveView.swift. Premium-gated by the
// parent (this whole block is only rendered when isPremium is true).
//
// State machine:
//   idle        → "Download archive for offline (premium)" button
//   downloading → "Downloading 47/120…" + linear progress bar
//   complete    → "120 puzzles · 600 KB available offline · Clear"

function OfflineSection() {
  const [state, setState] = useState<ArchiveDlState>({ kind: 'idle' });
  // Mirror iOS's `alwaysDownloadArchive` toggle — when on, every page
  // load auto-tops-up the offline cache. Stored in localStorage.
  const [alwaysOn, setAlwaysOn] = useState<boolean>(() => getAlwaysDownloadEnabled());
  // Avoid setState after unmount — the download is fire-and-forget and
  // can outlive the modal if the user closes mid-run.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // On first mount, surface any prior session's downloaded bytes so the
  // user sees "X puzzles available offline" without re-running.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await summarizePinned();
      if (cancelled || !mounted.current) return;
      setState(s);
    })();
    return () => { cancelled = true; };
  }, []);

  function toggleAlways(next: boolean) {
    setAlwaysOn(next);
    setAlwaysDownloadEnabled(next);
  }

  const start = () => {
    setState({ kind: 'downloading', done: 0, total: 1 });
    void runArchiveDownload(null, (s) => {
      if (mounted.current) setState(s);
    });
  };

  const clear = async () => {
    if (!window.confirm('Remove all downloaded puzzles?')) return;
    await clearPinned();
    if (mounted.current) setState({ kind: 'idle' });
  };

  return (
    <div className="hm-archive-offline">
      <div className="hm-archive-offline-label">OFFLINE</div>
      {state.kind === 'idle' && (
        <button
          type="button"
          className="hm-primary hm-archive-offline-btn"
          onClick={start}
        >
          Download archive for offline (premium)
        </button>
      )}
      {state.kind === 'downloading' && (
        <div className="hm-archive-offline-progress">
          <div className="hm-archive-offline-progress-label">
            Downloading {state.done}/{state.total}…
          </div>
          <progress
            className="hm-archive-offline-bar"
            value={state.done}
            max={Math.max(state.total, 1)}
          />
        </div>
      )}
      {state.kind === 'complete' && (
        <div className="hm-archive-offline-summary">
          <span className="hm-archive-offline-text">
            {state.count} puzzles · {humanReadableBytes(state.bytes)} available offline
          </span>
          <button
            type="button"
            className="hm-archive-offline-clear"
            onClick={clear}
            aria-label="Clear downloaded puzzles"
          >
            Clear
          </button>
        </div>
      )}
      <label className="hm-archive-offline-always">
        <input
          type="checkbox"
          checked={alwaysOn}
          onChange={(e) => toggleAlways(e.target.checked)}
        />
        <span>Always download new puzzles</span>
        <span className="hm-archive-offline-always-hint">
          Auto-tops-up the offline cache on every visit. Skips puzzles you already have.
        </span>
      </label>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────

function Paywall({ message, onUpgrade }: { message: string; onUpgrade: () => void }) {
  return (
    <div className="hm-archive-paywall">
      <div className="hm-archive-paywall-glyph" aria-hidden="true">★</div>
      <h3>Past-day archive</h3>
      <p>{message}</p>
      <button type="button" className="hm-primary" onClick={onUpgrade}>
        Upgrade to Premium
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────

function prettyDate(s: string): string {
  // s = YYYY-MM-DD in America/New_York. Render as e.g. "May 11" — drop
  // the year (shown small underneath). Locale-agnostic month list keeps
  // the layout consistent regardless of the user's browser locale.
  const [, m, d] = s.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[parseInt(m, 10) - 1] ?? '';
  const day = parseInt(d, 10);
  return `${month} ${day}`;
}

function buildDateList(): string[] {
  // Descend from today (ET) to MIN_ARCHIVE_DATE. Bound by year boundaries
  // — same calculation as iOS's allArchiveDates.
  const today = dailyDateString();
  const out: string[] = [];
  let cursor = today;
  while (cursor >= MIN_ARCHIVE_DATE) {
    out.push(cursor);
    cursor = shiftDate(cursor, -1);
    if (out.length > 800) break; // safety: ~2 years
  }
  return out;
}

function shiftDate(s: string, delta: number): string {
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return dt.toISOString().slice(0, 10);
}
